import { MEMORY_HIDDEN_TEXT, type MemoryPart } from '@contracts/assistant-memory';

/**
 * Small helpers shared by the lists of notes (the memory page, the "Em đã nhớ" line, the admin's company knowledge):
 * a row button's accessible name, and where the focus goes when a row or its edit box leaves the screen (S3 L4 review
 * U-F1, U-F3).
 */

/** How much of a note a row button's name carries: enough to tell rows apart, short enough to listen to. */
const NAME_MAX = 40;

/** A note's words as a reader hears them: an object by its name, a hidden one by the hidden words. */
export const memoryPlainOf = (parts: readonly MemoryPart[]): string => parts.map((part) => {
  if ('text' in part) return part.text;
  if ('hidden' in part) return MEMORY_HIDDEN_TEXT;
  return part.ref.label;
}).join('').replace(/\s+/gu, ' ').trim();

/** "Xoá: Muốn hạn mặc định là thứ 6": the verb and its note, cut at a word near 40 characters. */
export function rowActionName(verb: string, text: string): string {
  const plain = text.replace(/\s+/gu, ' ').trim();
  if (plain.length <= NAME_MAX) return `${verb}: ${plain}`;
  const head = plain.slice(0, NAME_MAX + 1);
  const space = head.lastIndexOf(' ');
  const cut = space > NAME_MAX / 2 ? head.slice(0, space) : plain.slice(0, NAME_MAX);
  return `${verb}: ${cut.trimEnd()}…`;
}

/** Marks a list's group heading, so a removal can hand it the focus. */
export const GROUP_HEADING = 'data-memory-group-heading';

/**
 * Where the focus goes once `row` leaves its list: the same control (`control`, a selector) on the next row, else on
 * the row before; with the group then empty, the heading of the nearest group that stays (the next one, else the one
 * before) inside `scope`; else `fallback`. Chosen while the row is still there, so it is always something that stays.
 */
export function focusAfterRemoval(row: Element | null, control: string, scope: Element | null, fallback: () => HTMLElement | null) {
  const on = (el: Element | null | undefined) => el?.querySelector<HTMLElement>(control) ?? null;
  const sibling = on(row?.nextElementSibling) ?? on(row?.previousElementSibling);
  if (sibling !== null) return sibling;
  const own = row?.closest('section') ?? null;
  const headings = scope === null ? [] : [...scope.querySelectorAll<HTMLElement>(`[${GROUP_HEADING}]`)]
    .filter((heading) => own === null || !own.contains(heading));
  const after = own === null ? undefined
    : headings.find((heading) => (own.compareDocumentPosition(heading) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0);
  return after ?? headings[headings.length - 1] ?? fallback();
}
